import { NgModule, APP_INITIALIZER, inject, EnvironmentProviders, Provider, LOCALE_ID, ErrorHandler } from '@angular/core';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { BrowserModule } from '@angular/platform-browser';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { SharedModule } from './shared/shared.module';
import { CoreModule } from './core/core.module';
import { NAVIGATION } from './constant/navigation';
import { NAVIGATION_ITEMS } from './layout/navigation.service';
import { LayoutModule } from './layout/layout.module';
import { ApiModule } from './api/api.module';
import { FeatureFlagKeys, FeatureFlagService } from './shared/directives/feature-flag/feature-flag.service';
import { GoogleAnalyticsService } from './google-analytics.service';
import { SanitizerService } from './sanitizer.service';
import { IPublicClientApplication, PublicClientApplication, InteractionType, LogLevel, BrowserCacheLocation } from '@azure/msal-browser';
import { MsalInterceptorConfiguration, MSAL_INTERCEPTOR_CONFIG, MsalGuardConfiguration, MSAL_GUARD_CONFIG, MSAL_INSTANCE, MsalService, MsalGuard, MsalBroadcastService, MsalInterceptor, ProtectedResourceScopes } from '@azure/msal-angular';
import { environment } from '@env/environment';
import { AuthHeaderInterceptor } from './core/auth/interceptors/auth-header.interceptor';
import { ApiInterceptor } from './core/auth/interceptors/api.interceptor';
import { ErrorInterceptor } from './core/auth/interceptors/error.interceptor';
import { AuthService } from './core/auth/auth.service';
import { AuthUserActivityService } from './core/auth/auth-user-activity.service';
import { AuthTokenService } from './core/auth/auth-token.service';
import { AuthTokenStorageService } from './core/auth/auth-token-storage.service';
import { GlobalErrorHandler } from './core/global-error-handler';
import { MainInfoResolver } from './layout/main/main.info-resolver';
import { providePrimeNG } from 'primeng/config';
import Aura from '../../node_modules/@primeuix/themes/dist/aura/index.mjs';

function initializeFeatureFlag(): () => Record<FeatureFlagKeys, boolean> {
  const featureFlagService = inject(FeatureFlagService);
  return () => featureFlagService.getFeatureFlags();
}

function MSALInstanceFactory(): IPublicClientApplication {
  if (!environment.b2c) {
    throw new Error('MSAL configuration is missing: environment.b2c is undefined. Check your environment setup.');
  }

  const locale = inject(LOCALE_ID);
  return new PublicClientApplication({
    auth: {
      clientId: environment.b2c.clientId,
      authority: `https://${environment.b2c.environment}/${environment.b2c.tenantId}/${environment.b2c.policyId}`,
      knownAuthorities: [environment.b2c.environment],
      redirectUri: `${environment.baseUrl.value}${!environment.baseUrl.isLocalizedPath ? `/${locale}` : ""}/`, // Try document.location.origin too
      postLogoutRedirectUri: `${environment.baseUrl.value}${!environment.baseUrl.isLocalizedPath ? `/${locale}` : ""}/`, // Try document.location.origin too
    },
    cache: {
      cacheLocation: BrowserCacheLocation.SessionStorage, // TODO - do some more investigation on what is better
      storeAuthStateInCookie: window.navigator.userAgent.indexOf('MSIE ') > -1 || window.navigator.userAgent.indexOf('Trident/') > -1,
    },
    system: {
      allowPlatformBroker: false, // Disables WAM Broker
      loggerOptions: {
        loggerCallback: (level: LogLevel, message: string) => console.log(message),
        logLevel: LogLevel.Info,
        piiLoggingEnabled: false,
      },
      // Enable triggering silent refresh (for access/ID tokens) before expiry
      // tokenRenewalOffsetSeconds: 5*60 - 20, // Start renewal with an offset before expiry
    },
  });
}

function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  if (!environment.b2c) {
    throw new Error('MSAL configuration is missing: environment.b2c is undefined. Check your environment setup.');
  }

  const protectedResourceMap = new Map<string, (string | ProtectedResourceScopes)[] | null>();
  for (const apiService of Object.values(environment.api.services)) {
    if (typeof apiService !== 'string') {
      const msalProtectedApiIdentifierUri = `https://${environment.b2c.tenantId}/${environment.b2c.protectedApiIdentifier}`;
      protectedResourceMap.set(
        `${environment.api.baseUrl ?? ""}${apiService.url}`,
        apiService.scopes.map(scope => `${msalProtectedApiIdentifierUri}/${scope}`),
      );
    }
  }
  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

function MSALGuardConfigFactory(): MsalGuardConfiguration {
  if (!environment.b2c) {
    throw new Error('MSAL configuration is missing: environment.b2c is undefined. Check your environment setup.');
  }

  const locale = inject(LOCALE_ID);
  const b2cLocale = locale.toLowerCase() !== 'pt-br' ? locale.split('-')[0] : locale;
  const msalProtectedApiIdentifierUri = `https://${environment.b2c.tenantId}/${environment.b2c.protectedApiIdentifier}`;
  const minimalAccessScopes = environment.api.minimalAccessScopes.map(scope => `${msalProtectedApiIdentifierUri}/${scope}`);
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: ['openid', 'profile', ...minimalAccessScopes],
      extraQueryParameters: {
        ui_locales: b2cLocale,
        // Add other parameters like loginHint, domainHint, rerun_connectors, prompt, etc.
      },
    },
    loginFailedRoute: `${environment.baseUrl.value}${environment.baseUrl.isLocalizedPath ? `/${locale}` : ""}/error`, // Try `${document.location.origin}/error` too
  };
}

function initializeProvidersForAuthentication(): (Provider | EnvironmentProviders)[] {
  if (environment.b2c === undefined) {
    return [
      {
        provide: HTTP_INTERCEPTORS,
        useClass: AuthHeaderInterceptor,
        multi: true,
      },
      {
        provide: HTTP_INTERCEPTORS,
        useClass: ApiInterceptor, 
        multi: true,
      },
      {
        provide: HTTP_INTERCEPTORS,
        useClass: ErrorInterceptor,
        multi: true,
      },
      AuthService,
      AuthUserActivityService,
      AuthTokenService,
      AuthTokenStorageService,
    ]
  };
  return [
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    // To insure the MSAL service instance is completely initialized when the 
    // app starts.
    {
      provide: APP_INITIALIZER,
      useFactory: (msalService: MsalService) => () => msalService.instance.initialize(),
      deps: [MsalService],
      multi: true
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
  ];
}

@NgModule({ declarations: [
        AppComponent
    ],
    bootstrap: [AppComponent], imports: [BrowserModule,
        BrowserAnimationsModule,
        AppRoutingModule,
        LayoutModule,
        CoreModule,
        ApiModule,
        SharedModule], providers: [
        {
            provide: NAVIGATION_ITEMS,
            useValue: NAVIGATION,
        },
        {
            provide: APP_INITIALIZER,
            useFactory: initializeFeatureFlag,
            deps: [],
            multi: true,
        },
        ...initializeProvidersForAuthentication(),
        GoogleAnalyticsService,
        SanitizerService,
        {
            provide: ErrorHandler,
            useClass: GlobalErrorHandler
        },
        MainInfoResolver,
        provideHttpClient(withInterceptorsFromDi()),
        providePrimeNG({ theme: { preset: Aura } }),
    ] })
export class AppModule { }
